import { useState, useMemo } from "react";
import { ELEMENTS as elements } from "../data/periodicTable.js";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ChemBackButton } from "./ChemBackButton.jsx";

const EM = "#F8B84E", EM_DEEP = "#7C3AED", EM_LIGHT = "#FCD9A0";
const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};
const VALID = new Set(elements.map(e => e.symbol));

// ── exact rational arithmetic (BigInt) — no floating point, so the
//    coefficients are mathematically exact, never approximated ──
const gcd = (a,b)=>{a=a<0n?-a:a;b=b<0n?-b:b;while(b){[a,b]=[b,a%b];}return a;};
function rat(n,d=1n){ n=BigInt(n); d=BigInt(d);
  if(d===0n) throw new Error("div0");
  if(d<0n){n=-n;d=-d;}
  const g=gcd(n,d)||1n; return [n/g, d/g]; }
const rMul=([a,b],[c,d])=>rat(a*c, b*d);
const rDiv=([a,b],[c,d])=>rat(a*d, b*c);
const rSub=([a,b],[c,d])=>rat(a*d-c*b, b*d);
const rIsZero=([a])=>a===0n;

// ── verified formula parser (identical logic to MolecularWeight, 13/13) ──
function parseFormula(raw){
  if(!raw||!raw.trim())return{counts:null,error:"empty"};
  const f=raw.replace(/[·•∙*]/g,".").trim();
  const segs=[];let buf="";
  for(let i=0;i<f.length;i++){const ch=f[i];
    if(ch==="."&&/[0-9A-Z]/.test(f[i+1]||"")){segs.push(buf);buf="";}else buf+=ch;}
  segs.push(buf);
  // An empty segment means a leading/trailing/doubled hydrate dot
  // (e.g. ".5H2O" or "CuSO4..5H2O") — malformed, not silently skipped.
  if(segs.length>1&&segs.some(x=>!x.trim()))return{counts:null,error:"Malformed formula (stray separator)"};
  const total={};
  for(let seg of segs){seg=seg.trim();if(!seg)continue;
    let mult=1;const m=seg.match(/^(\d+)(?=[A-Z(\[])/);
    if(m){mult=parseInt(m[1]);seg=seg.slice(m[1].length);}
    const r=parseSeg(seg);if(r.error)return{counts:null,error:r.error};
    for(const[el,n]of Object.entries(r.counts))total[el]=(total[el]||0)+n*mult;}
  return{counts:total,error:null};
}
function parseSeg(s){const st=[{}];const bk=[];let i=0;
  while(i<s.length){const ch=s[i];
    if(ch==="("||ch==="["){st.push({});bk.push(ch);i++;continue;}
    if(ch===")"||ch==="]"){i++;let num="";
      while(i<s.length&&/\d/.test(s[i]))num+=s[i++];
      const mu=num?parseInt(num):1;
      if(!bk.length)return{counts:null,error:"Unbalanced parentheses"};
      const open=bk.pop();
      if((open==="("&&ch!==")")||(open==="["&&ch!=="]"))return{counts:null,error:"Mismatched bracket types"};
      const top=st.pop();
      if(!st.length)return{counts:null,error:"Unbalanced parentheses"};
      const into=st[st.length-1];
      for(const[el,n]of Object.entries(top))into[el]=(into[el]||0)+n*mu;continue;}
    const sym=s.slice(i).match(/^[A-Z][a-z]?/);
    if(!sym)return{counts:null,error:`Unexpected character "${ch}"`};
    const el=sym[0];if(!VALID.has(el))return{counts:null,error:`Unknown element "${el}"`};
    i+=el.length;let num="";
    while(i<s.length&&/\d/.test(s[i]))num+=s[i++];
    const c=num?parseInt(num):1;const top=st[st.length-1];
    top[el]=(top[el]||0)+c;}
  if(st.length!==1||bk.length)return{counts:null,error:"Unbalanced parentheses"};
  return{counts:st[0],error:null};
}

function balance(eqn){
  const parts=eqn.split(/=|->|→|⟶/);
  if(parts.length!==2) return {error:"Use one '=' or '->' between reactants and products"};
  const [lhsS,rhsS]=parts.map(s=>s.trim());
  if(!lhsS||!rhsS) return {error:"Both sides of the equation are required"};
  const left=lhsS.split("+").map(s=>s.trim()).filter(Boolean);
  const right=rhsS.split("+").map(s=>s.trim()).filter(Boolean);
  if(!left.length||!right.length) return {error:"Need at least one species on each side"};
  const species=[...left,...right];
  const parsed=[];
  for(const sp of species){
    const {counts,error}=parseFormula(sp);
    if(error) return {error:`"${sp}": ${error}`};
    parsed.push(counts);
  }
  const els=[...new Set(parsed.flatMap(c=>Object.keys(c)))];
  const A=els.map(el=>parsed.map((c,j)=>{
    const v=BigInt(c[el]||0);
    return rat(j<left.length? v : -v);
  }));
  const nS=species.length;
  let row=0; const piv=[];
  for(let col=0; col<nS && row<A.length; col++){
    let sel=-1;
    for(let r=row;r<A.length;r++) if(!rIsZero(A[r][col])){sel=r;break;}
    if(sel<0) continue;
    [A[row],A[sel]]=[A[sel],A[row]];
    const pv=A[row][col];
    A[row]=A[row].map(x=>rDiv(x,pv));
    for(let r=0;r<A.length;r++){ if(r!==row && !rIsZero(A[r][col])){
      const fac=A[r][col];
      A[r]=A[r].map((x,k)=>rSub(x,rMul(fac,A[row][k])));
    }}
    piv.push(col); row++;
  }
  const free=[]; for(let c=0;c<nS;c++) if(!piv.includes(c)) free.push(c);
  if(free.length===0) return {error:"This equation has no balancing solution"};
  if(free.length>1) return {error:"Ambiguous — looks like more than one independent reaction"};
  const fc=free[0];
  const sol=new Array(nS).fill(null).map(()=>rat(0));
  sol[fc]=rat(1);
  for(let i=0;i<piv.length;i++) sol[piv[i]]=rSub(rat(0), A[i][fc]);
  let L=1n; for(const [,d] of sol) L = L/gcd(L,d)*d;
  let ints=sol.map(([n,d])=> n * (L/d));
  let G=0n; for(const v of ints) G=gcd(G, v<0n?-v:v); if(G===0n)G=1n;
  ints=ints.map(v=>v/G);
  if(ints.every(v=>v<=0n)) ints=ints.map(v=>-v);
  if(ints.some(v=>v<=0n)) return {error:"No positive whole-number solution exists"};
  return { coeffs: ints.map(v=>Number(v)), left, right };
}

// Render a formula with subscripted digits for display.
function Formula({ text }) {
  const parts = text.split(/(\d+)/);
  return <>{parts.map((p,i)=> /^\d+$/.test(p)
    ? <sub key={i} style={{ fontSize: "0.7em" }}>{p}</sub>
    : <span key={i}>{p}</span>)}</>;
}

export default function EquationBalancer() {
  const [input, setInput] = useState("");
  const result = useMemo(() => {
    if (!input.trim()) return null;
    try { return balance(input); }
    catch { return { error: "Could not parse that equation" }; }
  }, [input]);

  const examples = [
    "H2 + O2 = H2O",
    "CH4 + O2 = CO2 + H2O",
    "Fe + O2 = Fe2O3",
    "KMnO4 + HCl = KCl + MnCl2 + H2O + Cl2",
    "C6H12O6 + O2 = CO2 + H2O",
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(EM,0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(EM,0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ChemBackButton accentColor={EM} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#fff" }}>Ch</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${EM_LIGHT}, #FDE9C8)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Equation Balancer</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdChem</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 620, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <p style={{ fontSize: 15, color: "#9CA3AF", lineHeight: 1.6, textAlign: "center", margin: "0 0 24px" }}>
          Type an unbalanced equation and get the balanced form with the
          smallest whole-number coefficients. Uses exact arithmetic — no
          rounding, no wrong coefficients. Separate species with <code style={{ color: EM_LIGHT }}>+</code> and
          sides with <code style={{ color: EM_LIGHT }}>=</code> or <code style={{ color: EM_LIGHT }}>-&gt;</code>.
        </p>

        <input
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. C3H8 + O2 = CO2 + H2O"
          style={{ width: "100%", padding: "16px 18px", borderRadius: 12, border: `1px solid ${rgba(EM,0.25)}`, background: "rgba(0,0,0,0.3)", color: "#E2E8F0", fontSize: 18, fontFamily: "inherit", outline: "none", boxSizing: "border-box", textAlign: "center", fontWeight: 600 }}
        />

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginTop: 14 }}>
          {examples.map((ex) => (
            <button key={ex} onClick={() => setInput(ex)} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)", color: "#9CA3AF", fontSize: 13, fontFamily: "inherit", cursor: "pointer" }}>{ex}</button>
          ))}
        </div>

        {result?.error && (
          <div style={{ marginTop: 28, padding: "16px 20px", borderRadius: 12, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.25)", color: "#FDA4AF", fontSize: 14, textAlign: "center" }}>
            {result.error}
          </div>
        )}

        {result && !result.error && (
          <div style={{ marginTop: 28, padding: "28px 22px", borderRadius: 16, background: rgba(EM,0.07), border: `1px solid ${rgba(EM,0.25)}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", marginBottom: 14 }}>Balanced equation</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: EM_LIGHT, textAlign: "center", lineHeight: 1.8, wordBreak: "break-word" }}>
              {result.left.map((sp, i) => (
                <span key={`l${i}`}>
                  {i > 0 && <span style={{ color: "#6B7280", margin: "0 8px" }}>+</span>}
                  {result.coeffs[i] !== 1 && <span style={{ color: "#fff" }}>{result.coeffs[i]}</span>}
                  <Formula text={sp} />
                </span>
              ))}
              <span style={{ color: EM, margin: "0 14px", fontWeight: 800 }}>→</span>
              {result.right.map((sp, i) => {
                const ci = result.left.length + i;
                return (
                  <span key={`r${i}`}>
                    {i > 0 && <span style={{ color: "#6B7280", margin: "0 8px" }}>+</span>}
                    {result.coeffs[ci] !== 1 && <span style={{ color: "#fff" }}>{result.coeffs[ci]}</span>}
                    <Formula text={sp} />
                  </span>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: "#6B7280", textAlign: "center", marginTop: 18 }}>
              Coefficients: {result.coeffs.join(" : ")}
            </div>
          </div>
        )}
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM,0.04)}` }}>
        <NerdStackFooter variant="tool" family="chem" />
      </footer>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); input::placeholder { color: #4B5563; } code { font-family: ui-monospace, monospace; font-size: 0.9em; } * { box-sizing: border-box; } body { margin: 0; }`}</style>
    </div>
  );
}
